import type { RouteObject } from "react-router-dom";
import { lazy } from "react";
import { Spinner } from "@/components/ui/v2/spinner";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { CreateTeams } from "@/pages/(home)/(company)/teams/create";
import { UpsertTeamRoles } from "@/pages/(home)/(company)/teams/settings/_components/upsert-team-roles";
import {
  DYNAMIC_TEAM_ROLE_ID_KEY,
  MODULE_SETTINGS_PATHS,
} from "./module-settings.paths";

// Re-exported for the components/route files that already reference it here.
export { DYNAMIC_TEAM_ROLE_ID_KEY };

// Lazy so the route file doesn't eagerly pull the tabs page (which imports
// nav-tabs.config) during config initialization — avoids a circular-import TDZ.
const CompanyTeamsPage = lazy(() =>
  import("@/pages/(home)/(company)/teams").then((m) => ({
    default: m.CompanyTeamsPage,
  })),
);
const CompanyTeamsListPage = lazy(
  () => import("@/pages/(home)/(company)/teams/list"),
);
const CompanyTeamSettingsPage = lazy(() =>
  import("@/pages/(home)/(company)/teams/settings").then((m) => ({
    default: m.CompanyTeamSettings,
  })),
);


export enum COMPANY_TEAMS_PATH_SEGMENTS {
  ROOT = "teams",
  CREATE = "create",
  DETAILS = `:teamId`,
  EDIT = "edit",
  // ── Module settings (moved out of Company Settings) ──────────────────────
  // Literal, not `MODULE_SETTINGS_SEGMENT`: `isolatedModules` forbids enum
  // members initialized from an imported const. Keep both in sync.
  SETTINGS = "settings",
  TEAM_ROLES = "team-roles",
  TEAM_ROLE_DETAILS = `:${DYNAMIC_TEAM_ROLE_ID_KEY}`,
}

export const companyTeamsRoutes: RouteObject = {
  path: COMPANY_TEAMS_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Teams" },
  HydrateFallback: Spinner,
  children: [
    // ── Page-level tabs layout (single "Teams" tab at the module root) ───────
    {
      Component: CompanyTeamsPage,
      handle: { flushContent: true },
      children: [{ index: true, Component: CompanyTeamsListPage }],
    },
    {
      path: COMPANY_TEAMS_PATH_SEGMENTS.CREATE,
      handle: { breadcrumb: "Create Team" },
      Component: CreateTeams,
    },

    // ── Module settings — a SIBLING of the tabs layout, never a child of it:
    // nesting it inside would render it under a tab bar with no matching tab
    // (same pitfall documented for the performance-metrics routes). Static
    // "settings" also outranks the `:teamId` sibling below in React Router's
    // ranking, so there is no collision. ────────────────────────────────────
    {
      path: COMPANY_TEAMS_PATH_SEGMENTS.SETTINGS,
      handle: { breadcrumb: "Settings", title: "Team Settings" },
      children: [
        { index: true, Component: CompanyTeamSettingsPage },
        {
          path: COMPANY_TEAMS_PATH_SEGMENTS.TEAM_ROLES,
          handle: { breadcrumb: "Roles & Responsibilities" },
          children: [
            { index: true, Component: UpsertTeamRoles },
            {
              path: COMPANY_TEAMS_PATH_SEGMENTS.CREATE,
              handle: { breadcrumb: "Create Role", title: "Create Team Role" },
              Component: UpsertTeamRoles,
            },
            {
              path: COMPANY_TEAMS_PATH_SEGMENTS.TEAM_ROLE_DETAILS,
              children: [
                { index: true, Component: UpsertTeamRoles },
                {
                  path: COMPANY_TEAMS_PATH_SEGMENTS.EDIT,
                  handle: { breadcrumb: "Edit Role", title: "Edit Team Role" },
                  Component: UpsertTeamRoles,
                },
              ],
            },
          ],
        },
      ],
    },

    {
      path: COMPANY_TEAMS_PATH_SEGMENTS.DETAILS,
      children: [
        {
          index: true,
          Component: CreateTeams,
        },
        {
          path: COMPANY_TEAMS_PATH_SEGMENTS.EDIT,
          handle: { breadcrumb: "Edit Team" },
          Component: CreateTeams,
        },
      ],
    },
  ],
};

export const COMPANY_TEAMS_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_TEAMS_PATH_SEGMENTS.ROOT)),
  CREATE: withCompanyPrefix(addSlashes(COMPANY_TEAMS_PATH_SEGMENTS.ROOT, COMPANY_TEAMS_PATH_SEGMENTS.CREATE)),
  VIEW: (id: string | number) => withCompanyPrefix(addSlashes(COMPANY_TEAMS_PATH_SEGMENTS.ROOT, String(id))),
  EDIT: (id: string | number) =>
    withCompanyPrefix(addSlashes(COMPANY_TEAMS_PATH_SEGMENTS.ROOT, String(id), COMPANY_TEAMS_PATH_SEGMENTS.EDIT)),

  // ── Module settings (resolved in module-settings.paths.ts) ────────────────
  SETTINGS: MODULE_SETTINGS_PATHS.TEAMS.ROOT,
  SETTINGS_VTAB_ROLES_RESPS: MODULE_SETTINGS_PATHS.TEAMS.VTAB_ROLES_RESPS,
  SETTINGS_TEAM_ROLES: MODULE_SETTINGS_PATHS.TEAMS.TEAM_ROLES,
  SETTINGS_TEAM_ROLE_CREATE: MODULE_SETTINGS_PATHS.TEAMS.TEAM_ROLE_CREATE,
  SETTINGS_TEAM_ROLE_VIEW: MODULE_SETTINGS_PATHS.TEAMS.TEAM_ROLE_VIEW,
  SETTINGS_TEAM_ROLE_EDIT: MODULE_SETTINGS_PATHS.TEAMS.TEAM_ROLE_EDIT,
});
